import { isAssistantSearchHitsEmpty } from './assistantSearchHits';
import type { AppView } from '../navigation/AppNavigationContext';
import { ISRAEL_COUNTRY_ID } from '../utils/assetCountryCurrency';
import type { AssistantSearchIntent } from './parseAssistantIntent';
import { mergeAssistantScheduleOverlay } from './assistantSchedule';

function citySearchToken(city: string): string {
  return city.split(/\s*[-–]\s*/)[0]?.trim() || city.trim();
}

export function isResetSearchUtterance(raw: string): boolean {
  return /^(new search|start over|start again|reset search|start a new search|start new search|another search|חיפוש חדש|חיפוש מחדש|התחל מחדש|התחילי מחדש|תתחיל מחדש|נתחיל מחדש)$/i.test(
    raw.trim(),
  );
}

export function isClearChatUtterance(raw: string): boolean {
  return /^(clear|clear chat|clear search|erase|נקה|נקה חיפוש|מחק|מחק הכל)$/i.test(raw.trim());
}

export function isFollowUpSearchUtterance(raw: string): boolean {
  return /(^|\s)(only|just|also|filter|narrow|from these|in these|among these|within these|רק|מתוך|בתוצאות|סנן|צמצם)(\s|$)/i.test(
    raw.trim(),
  );
}

/**
 * Named business / venue leftover (e.g. “תחשוב עסקים”, “onobiz”).
 * Used as an extra name filter on the current city/street results.
 */
export function isNamedVenueSearch(intent: AssistantSearchIntent): boolean {
  return Boolean(intent.venue?.trim());
}

function hasSearchType(intent: AssistantSearchIntent): boolean {
  return Boolean(intent.assetTypeLabel?.trim() || intent.officeFamily);
}

function hasSearchPlace(intent: AssistantSearchIntent): boolean {
  return Boolean(
    intent.city?.trim() ||
      intent.street?.trim() ||
      intent.streetDisplay?.trim() ||
      intent.proximityStreet?.trim(),
  );
}

/**
 * Type + place together is a full catalog search (“קליניקה בחיפה”), not a refine.
 * Modifiers alone (date, price, street, venue, type-only) stay follow-ups.
 */
export function isCompleteSearchAnchor(intent: AssistantSearchIntent): boolean {
  return hasSearchType(intent) && hasSearchPlace(intent);
}

/**
 * Merge when the utterance only adds filters to the current results.
 * Start a new search when the query is a complete type+place anchor, when the
 * user says “חיפוש חדש”, or when the current hit list is empty.
 */
export function shouldMergeAssistantSearch(
  previous: AssistantSearchIntent | null,
  next: AssistantSearchIntent,
  raw: string,
  _view?: AppView | null,
): boolean {
  if (!previous) {
    return false;
  }
  if (isResetSearchUtterance(raw) || isClearChatUtterance(raw)) {
    return false;
  }
  if (isAssistantSearchHitsEmpty()) {
    return false;
  }
  if (isCompleteSearchAnchor(next)) {
    return false;
  }
  // “משרד במועדפים שלי” is a new favorites pool search, not a refine of the last city dump.
  if (next.favoritesOnly && hasSearchType(next)) {
    return false;
  }
  return true;
}

function mergedKeyword(intent: AssistantSearchIntent): string {
  const cityText = intent.city ? citySearchToken(intent.city) : '';
  if (intent.street) {
    return [intent.venue, intent.street, cityText].filter(Boolean).join(' ').trim();
  }
  if (intent.venue) {
    return [intent.venue, cityText].filter(Boolean).join(' ').trim();
  }
  if (intent.assetTypeLabel && cityText) {
    return `${intent.assetTypeLabel} ${cityText}`.trim();
  }
  return cityText || intent.keyword.trim();
}

function cityMergeKey(city: string | null | undefined): string {
  return (city?.split(/\s*[-–]\s*/)[0] ?? '').trim().toLowerCase();
}

/** Overlay a follow-up search onto the previous result filters. */
export function mergeAssistantSearchIntents(
  previous: AssistantSearchIntent,
  next: AssistantSearchIntent,
): AssistantSearchIntent {
  const city = next.city || previous.city;
  const cityChanged =
    Boolean(next.city?.trim()) &&
    Boolean(previous.city?.trim()) &&
    cityMergeKey(next.city) !== cityMergeKey(previous.city);
  const merged: AssistantSearchIntent = {
    kind: 'search',
    assetTypeLabel: next.assetTypeLabel || previous.assetTypeLabel,
    city,
    countryId: city ? ISRAEL_COUNTRY_ID : next.countryId ?? previous.countryId,
    // “Closest to street X” must not keep a hard street filter from the previous turn.
    // A new city without a street must not keep the previous city’s street either
    // (e.g. Clinic + ביצרון from “באזור תל אביב” must not follow into חיפה).
    street: next.proximityStreet ? null : next.street ?? (cityChanged ? null : previous.street),
    streetDisplay: next.proximityStreet
      ? null
      : next.streetDisplay ??
        next.street ??
        (cityChanged ? null : previous.streetDisplay || previous.street),
    whenDate: next.whenDate || previous.whenDate,
    whenMonth: next.whenMonth || previous.whenMonth,
    whenTime: next.whenTime || previous.whenTime,
    open24h: Boolean(next.open24h || previous.open24h),
    amenityIcons:
      next.amenityIcons?.length || previous.amenityIcons?.length
        ? Array.from(
            new Set(
              [...(previous.amenityIcons ?? []), ...(next.amenityIcons ?? [])].filter(Boolean),
            ),
          )
        : null,
    price: next.price || previous.price || null,
    priceSort: next.priceSort ?? previous.priceSort ?? null,
    schedule: mergeAssistantScheduleOverlay(next.schedule, previous.schedule),
    proximityStreet: cityChanged
      ? next.proximityStreet || null
      : next.proximityStreet || previous.proximityStreet || null,
    proximityStreetDisplay: cityChanged
      ? next.proximityStreetDisplay || next.proximityStreet || null
      : next.proximityStreetDisplay ||
        previous.proximityStreetDisplay ||
        next.proximityStreet ||
        previous.proximityStreet ||
        null,
    // Proximity sort must not keep a leftover venue keyword (often garbage from the
    // previous utterance) — that becomes listSearch and can wipe all hits.
    venue: next.proximityStreet ? next.venue : next.venue || previous.venue,
    officeFamily: next.assetTypeLabel
      ? Boolean(next.officeFamily)
      : Boolean(previous.officeFamily),
    assetIds: next.assetIds?.length ? next.assetIds : previous.assetIds ?? null,
    favoritesOnly: Boolean(next.favoritesOnly || previous.favoritesOnly),
    keyword: '',
    raw: next.raw,
  };
  merged.keyword = mergedKeyword(merged) || next.keyword || previous.keyword;
  return merged;
}

export function resolveAssistantSearchIntent(
  next: AssistantSearchIntent,
  previous: AssistantSearchIntent | null,
  view?: AppView | null,
): { intent: AssistantSearchIntent; refined: boolean } {
  if (previous && shouldMergeAssistantSearch(previous, next, next.raw, view)) {
    return { intent: mergeAssistantSearchIntents(previous, next), refined: true };
  }
  return { intent: next, refined: false };
}
